/**
 * detectPlatform.js — which set of instructions this reader actually needs.
 * =================================================================================================
 *
 * ⭐ WHY SNIFF AT ALL, given everyone is told not to. The operator's point was about frustration:
 * *"particularly those not technically sophisticated, who just want to listen."* Install steps for
 * four platforms shown to everybody is four times the reading and three-quarters of it irrelevant.
 * Defaulting to the right one removes that, and the others stay one tap away.
 *
 * ⛔ SO THIS NEVER GATES ANYTHING. It picks a DEFAULT, nothing more. A wrong guess costs a reader one
 * tap on "Using something else?" — which is the only kind of sniffing that is safe, because the
 * guess is allowed to be wrong. Nothing is hidden and no capability is assumed from it.
 *
 * ⚠️ AND IT IS ORDERED DELIBERATELY, because the strings overlap:
 *
 *   - An iPad reports "Macintosh" in desktop-mode Safari, which is most iPads — so `maxTouchPoints`
 *     is what tells an iPad from a Mac, not the platform string.
 *   - Every iOS browser says "like Mac OS X", so a naive /mac/ test catches iPhones too.
 *   - Android contains "Linux", so Android has to be tested before Linux.
 *
 * ⓘ The returned values are the keys the help content is written against, not vendor names:
 * 'ios' | 'mac' | 'windows' | 'android' | 'other'.
 */

export const PLATFORMS = ['ios', 'mac', 'windows', 'android', 'other'];

/** What each key is called in front of a reader. */
export const PLATFORM_LABELS = {
  ios: 'iPhone or iPad',
  mac: 'Mac',
  windows: 'Windows',
  android: 'Android',
  other: 'Something else',
};

/**
 * @param {object} [nav] injectable for tests — anything with `userAgent`, `platform`,
 *   `maxTouchPoints`.
 * @returns {string} one of PLATFORMS
 */
export function detectPlatform(nav) {
  const n = nav || (typeof navigator !== 'undefined' ? navigator : null);
  if (!n) return 'other';

  const ua = String(n.userAgent || '');
  const plat = String(n.platform || '');
  const touch = Number(n.maxTouchPoints || 0);

  // iPhone and iPod are unambiguous; iPad is not — see the header.
  if (/iPhone|iPod/i.test(ua)) return 'ios';
  if (/iPad/i.test(ua)) return 'ios';
  // ⚠️ A touch-capable "Macintosh" is an iPad in desktop mode. A real Mac reports 0 or 1.
  if (/Macintosh|Mac OS X/i.test(ua) && touch > 1) return 'ios';

  // ⛔ Android before Linux: an Android UA contains both.
  if (/Android/i.test(ua)) return 'android';

  if (/Macintosh|Mac OS X/i.test(ua) || /^Mac/i.test(plat)) return 'mac';
  if (/Windows|Win32|Win64/i.test(ua) || /^Win/i.test(plat)) return 'windows';

  return 'other';
}

export default detectPlatform;
